<template>
  <div class="wc-dashboard">
    <!-- KPI Cards -->
    <v-row dense>
      <v-col cols="6" sm="6" md="3">
        <div class="kpi-card kpi-1">
          <div class="kpi-label">Termine</div>
          <div class="kpi-value"><number :from="0" :to="totals.totalAppointments" :duration="0.8" /></div>
          <div class="kpi-sub">Ø {{ totals.avgAppointmentsPerDay }} / Tag</div>
        </div>
      </v-col>
      <v-col cols="6" sm="6" md="3">
        <div class="kpi-card kpi-2">
          <div class="kpi-label">No-Show Rate</div>
          <div class="kpi-value">{{ totals.noShowRate }}%</div>
          <div class="kpi-sub">{{ (totals.statusDistribution || {}).noshow || 0 }} Termine</div>
        </div>
      </v-col>
      <v-col cols="6" sm="6" md="3">
        <div class="kpi-card kpi-3">
          <div class="kpi-label">Task-Quote</div>
          <div class="kpi-value">{{ totals.taskCompletionRate }}%</div>
          <div class="kpi-sub">{{ totals.tasksDone }} / {{ totals.totalTasks }} erledigt</div>
        </div>
      </v-col>
      <v-col cols="6" sm="6" md="3">
        <div class="kpi-card kpi-4">
          <div class="kpi-label">Schichten</div>
          <div class="kpi-value"><number :from="0" :to="totals.totalShifts" :duration="0.8" /></div>
          <div class="kpi-sub">{{ totals.totalEmployees }} Mitarbeiter</div>
        </div>
      </v-col>
    </v-row>

    <v-row dense class="mt-2">
      <v-col cols="6" sm="6" md="3">
        <div class="kpi-card kpi-5">
          <div class="kpi-label">Aufgaben</div>
          <div class="kpi-value"><number :from="0" :to="totals.totalTasks" :duration="0.8" /></div>
          <div class="kpi-sub">{{ totals.completionsCount }} Ämtli-Checks</div>
        </div>
      </v-col>
      <v-col cols="6" sm="6" md="3">
        <div class="kpi-card kpi-6">
          <div class="kpi-label">Nicht erschienen</div>
          <div class="kpi-value"><number :from="0" :to="(totals.statusDistribution || {}).noshow || 0" :duration="0.8" /></div>
          <div class="kpi-sub">No-Show Termine</div>
        </div>
      </v-col>
      <v-col cols="6" sm="6" md="3">
        <div class="kpi-card kpi-7">
          <div class="kpi-label">Check-Ins</div>
          <div class="kpi-value"><number :from="0" :to="(totals.statusDistribution || {}).checkedin || 0" :duration="0.8" /></div>
          <div class="kpi-sub">Erschienen</div>
        </div>
      </v-col>
      <v-col cols="6" sm="6" md="3">
        <div class="kpi-card kpi-8">
          <div class="kpi-label">Absagen</div>
          <div class="kpi-value"><number :from="0" :to="(totals.statusDistribution || {}).cancelled || 0" :duration="0.8" /></div>
          <div class="kpi-sub">Storniert</div>
        </div>
      </v-col>
    </v-row>

    <!-- Tabs -->
    <v-card class="mt-4 glass-card" flat>
      <v-tabs v-model="activeTab" background-color="transparent" dark grow show-arrows>
        <v-tab>Übersicht</v-tab>
        <v-tab>Mitarbeiter</v-tab>
        <v-tab>Ämtli & Tasks</v-tab>
        <v-tab>Heatmap</v-tab>
        <v-tab>Wochentag</v-tab>
        <v-tab>Schicht- & Center-Analyse</v-tab>
        <v-tab>Rohdaten</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab" style="background: transparent">
        <!-- TAB 1: ÜBERSICHT -->
        <v-tab-item>
          <div class="pa-4">
            <v-row>
              <v-col cols="12" md="6">
                <div class="chart-card">
                  <div class="chart-title">Termine & Aufgaben pro Tag</div>
                  <apexchart type="area" height="320" :options="trendOpts" :series="trendSeries" />
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="chart-card">
                  <div class="chart-title">Termin-Status-Verteilung</div>
                  <apexchart type="donut" height="320" :options="donutOpts" :series="donutSeries" />
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="chart-card">
                  <div class="chart-title">No-Shows & Absagen (Trend)</div>
                  <apexchart type="line" height="320" :options="noShowTrendOpts" :series="noShowTrendSeries" />
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="chart-card">
                  <div class="chart-title">Stündliche Verteilung (alle Tage)</div>
                  <apexchart type="bar" height="320" :options="hourlyBarOpts" :series="hourlyBarSeries" />
                </div>
              </v-col>
            </v-row>
          </div>
        </v-tab-item>

        <!-- TAB 2: MITARBEITER -->
        <v-tab-item>
          <div class="pa-4">
            <!-- Side-by-Side Comparison Panel -->
            <v-expand-transition>
              <div v-if="selectedEmployees && selectedEmployees.length >= 2" class="comparison-panel mb-4">
                <div class="d-flex align-center justify-space-between mb-3 px-2">
                  <div class="panel-header-title white--text font-weight-bold">
                    <v-icon left color="cyan lighten-2">mdi-compare</v-icon>
                    Mitarbeiter-Vergleich ({{ selectedEmployees.length }} ausgewählt)
                  </div>
                  <v-spacer />
                  <v-btn small color="red lighten-1" class="black--text" @click="selectedEmployees = []">
                    <v-icon left small>mdi-close</v-icon> Vergleich beenden
                  </v-btn>
                </div>

                <v-row>
                  <v-col cols="12" lg="4" md="5">
                    <div class="comparison-cards-container">
                      <div
                        v-for="emp in selectedEmployees"
                        :key="emp.id"
                        class="comparison-emp-card cursor-pointer"
                        @click="openEmployeeDetails(emp)">
                        <div class="d-flex align-center mb-2">
                          <v-avatar size="36" class="mr-3">
                            <img :src="emp.picture ? '/img/profilbilder/' + emp.picture : '/img/default-avatar.png'" style="object-fit: cover" />
                          </v-avatar>
                          <div>
                            <div class="comp-emp-name text-truncate" style="max-width: 180px">{{ emp.name }}</div>
                            <div class="comp-emp-sub text-caption">{{ emp.shifts }} Schichten · {{ emp.totalWorkHours }}h</div>
                          </div>
                        </div>
                        <v-divider dark class="my-2" style="opacity: 0.15"></v-divider>
                        <v-row dense class="text-center comp-stats-grid">
                          <v-col cols="3">
                            <div class="comp-stat-val font-weight-bold" :class="occupancyColorClass(emp.occupancyRate)">{{ emp.occupancyRate }}%</div>
                            <div class="comp-stat-lbl">Auslastung</div>
                          </v-col>
                          <v-col cols="3">
                            <div class="comp-stat-val font-weight-bold">{{ emp.appointments }}</div>
                            <div class="comp-stat-lbl">Termine</div>
                          </v-col>
                          <v-col cols="3">
                            <div class="comp-stat-val font-weight-bold" :class="rateColor(emp.taskCompletionRate) + '--text'">
                              {{ emp.taskCompletionRate }}%
                            </div>
                            <div class="comp-stat-lbl">Tasks</div>
                          </v-col>
                          <v-col cols="3">
                            <div class="comp-stat-val font-weight-bold" :class="noShowColor(emp.noShowRate) + '--text'">{{ emp.noShowRate }}%</div>
                            <div class="comp-stat-lbl">No-Show</div>
                          </v-col>
                        </v-row>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" lg="8" md="7">
                    <div class="chart-card py-2">
                      <div class="chart-title mb-1">Direkter Kennzahlen-Vergleich</div>
                      <apexchart type="bar" height="240" :options="comparisonChartOpts" :series="comparisonChartSeries" />
                    </div>
                  </v-col>
                </v-row>
              </div>
            </v-expand-transition>

            <v-row>
              <v-col cols="12" md="7">
                <div class="chart-card">
                  <div class="chart-title">Termine pro Mitarbeiter</div>
                  <apexchart type="bar" height="360" :options="empBarOpts" :series="empBarSeries" />
                </div>
              </v-col>
              <v-col cols="12" md="5">
                <div class="chart-card">
                  <div class="chart-title">Top 5 Performer</div>
                  <div v-for="(e, i) in topEmployees" :key="e.id" class="top-slot-row cursor-pointer" @click="openEmployeeDetails(e)">
                    <div class="rank">#{{ i + 1 }}</div>
                    <v-avatar size="36" class="mr-2">
                      <img :src="e.picture ? '/img/profilbilder/' + e.picture : '/img/default-avatar.png'" style="object-fit: cover" />
                    </v-avatar>
                    <div class="flex-grow-1">
                      <div class="slot-title">{{ e.name }}</div>
                      <div class="slot-sub">{{ e.shifts }} Schichten · {{ e.totalWorkHours }}h</div>
                    </div>
                    <div class="text-right">
                      <div class="slot-value">{{ e.appointments }}</div>
                      <div class="slot-sub" :class="occupancyColorClass(e.occupancyRate)">{{ e.occupancyRate }}% Ausl.</div>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>

            <v-data-table
              dark
              dense
              show-select
              item-key="id"
              v-model="selectedEmployees"
              class="mt-4 transparent-table"
              :headers="empHeaders"
              :items="data ? data.employeeStats : []"
              :items-per-page="20"
              sort-by="appointments"
              sort-desc>
              <template v-slot:item.name="{ item }">
                <div class="d-flex align-center cursor-pointer py-1" @click="openEmployeeDetails(item)">
                  <v-avatar size="32" class="mr-3 elevation-1">
                    <img :src="item.picture ? '/img/profilbilder/' + item.picture : '/img/default-avatar.png'" style="object-fit: cover" />
                  </v-avatar>
                  <span class="font-weight-medium white--text hover-underline">{{ item.name }}</span>
                </div>
              </template>
              <template v-slot:item.occupancyRate="{ item }">
                <div class="d-flex align-center" style="min-width: 110px">
                  <span class="mr-2 font-weight-bold" :class="occupancyColorClass(item.occupancyRate)">{{ item.occupancyRate }}%</span>
                  <v-progress-linear
                    dark
                    height="6"
                    :value="item.occupancyRate"
                    :color="occupancyColor(item.occupancyRate)"
                    style="width: 50px"
                    class="rounded"></v-progress-linear>
                </div>
              </template>
              <template v-slot:item.taskCompletionRate="{ item }">
                <v-chip x-small :color="rateColor(item.taskCompletionRate)" dark>{{ item.taskCompletionRate }}%</v-chip>
              </template>
              <template v-slot:item.noShowRate="{ item }">
                <v-chip x-small :color="noShowColor(item.noShowRate)" dark>{{ item.noShowRate }}%</v-chip>
              </template>
            </v-data-table>
          </div>
        </v-tab-item>

        <!-- TAB 3: ÄMTLI & TASKS -->
        <v-tab-item>
          <div class="pa-4">
            <v-row>
              <v-col cols="12" md="6">
                <div class="chart-card">
                  <div class="chart-title">Erledigungsrate pro Aufgabentyp</div>
                  <apexchart type="bar" height="320" :options="tplBarOpts" :series="tplBarSeries" />
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="chart-card">
                  <div class="chart-title">Tasks über Zeit</div>
                  <apexchart type="area" height="320" :options="taskTrendOpts" :series="taskTrendSeries" />
                </div>
              </v-col>
            </v-row>
            <v-data-table
              dark
              dense
              class="mt-4 transparent-table"
              :headers="tplHeaders"
              :items="data ? data.templateStats : []"
              :items-per-page="20"
              sort-by="usageCount"
              sort-desc>
              <template v-slot:item.completionRate="{ item }">
                <v-chip x-small :color="rateColor(item.completionRate)" dark>{{ item.completionRate }}%</v-chip>
              </template>
              <template v-slot:item.category="{ item }">
                <v-chip x-small dark>{{ item.category }}</v-chip>
              </template>
            </v-data-table>
          </div>
        </v-tab-item>

        <!-- TAB 4: HEATMAP -->
        <v-tab-item>
          <div class="pa-4">
            <div class="d-flex align-center mb-3">
              <v-select
                v-model="heatmapMetric"
                :items="[
                  { text: 'Termine', value: 'appointments' },
                  { text: 'Tasks', value: 'tasks' },
                  { text: 'Betreuung (Mitarbeiter)', value: 'staffing' },
                ]"
                dark
                hide-details
                dense
                outlined
                style="max-width: 280px" />
            </div>
            <div class="chart-card">
              <apexchart type="heatmap" height="420" :options="hmOpts" :series="hmSeries" />
            </div>
          </div>
        </v-tab-item>

        <!-- TAB 5: WOCHENTAG -->
        <v-tab-item>
          <div class="pa-4">
            <v-row>
              <v-col cols="12" md="6">
                <div class="chart-card">
                  <div class="chart-title">Ø Termine pro Wochentag</div>
                  <apexchart type="bar" height="300" :options="wdBarOpts" :series="wdBarSeries" />
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="chart-card">
                  <div class="chart-title">No-Show Rate pro Wochentag</div>
                  <apexchart type="bar" height="300" :options="wdNoShowOpts" :series="wdNoShowSeries" />
                </div>
              </v-col>
            </v-row>
            <v-data-table
              dark
              dense
              class="mt-4 transparent-table"
              :headers="wdHeaders"
              :items="weekdayItems"
              :items-per-page="7"
              hide-default-footer>
              <template v-slot:item.noShowRate="{ item }">
                <v-chip x-small :color="noShowColor(item.noShowRate)" dark>{{ item.noShowRate }}%</v-chip>
              </template>
            </v-data-table>
          </div>
        </v-tab-item>

        <!-- TAB 6: SCHICHT- & CENTER-ANALYSE -->
        <v-tab-item>
          <div class="pa-4">
            <v-row>
              <v-col cols="12" md="7">
                <div class="chart-card">
                  <div class="chart-title">Schicht-Typen Verteilung pro Center</div>
                  <apexchart type="bar" height="340" :options="centerShiftOpts" :series="centerShiftSeries" />
                </div>
              </v-col>
              <v-col cols="12" md="5">
                <div class="chart-card d-flex flex-column justify-space-between" style="min-height: 382px">
                  <div>
                    <div class="chart-title">Zusammenfassung & Auslastung pro Center</div>
                    <p class="text-caption grey--text text--lighten-2 mb-2">
                      Visualisierung der Center-Auslastung durch Termine (blau), Ämtli/Tasks (grün) und ungenutzte Schichtzeit (grau):
                    </p>

                    <v-divider dark class="my-2" style="opacity: 0.1"></v-divider>

                    <div v-for="c in centerBreakdownItems" :key="c.id" class="mb-4">
                      <div class="d-flex justify-space-between font-weight-bold white--text text-subtitle-2">
                        <div>{{ c.name }}</div>
                        <div :class="occupancyColorClass(c.totalOccupancyRate)">{{ c.totalOccupancyRate }}% Auslastung</div>
                      </div>

                      <!-- Custom Segmented Stacked Progress Bar -->
                      <div class="d-flex rounded overflow-hidden my-2 border border-dark" style="height: 22px; background: rgba(255, 255, 255, 0.06)">
                        <div
                          v-if="c.appointmentOccupancyRate > 0"
                          :style="{ width: c.appointmentOccupancyRate + '%' }"
                          class="bg-blue d-flex align-center justify-center text-caption font-weight-bold white--text select-none text-truncate"
                          :title="'Termine: ' + c.appointmentOccupancyRate + '%'">
                          {{ c.appointmentOccupancyRate > 8 ? c.appointmentOccupancyRate + "% Termine" : c.appointmentOccupancyRate + "%" }}
                        </div>
                        <div
                          v-if="c.taskOccupancyRate > 0"
                          :style="{ width: c.taskOccupancyRate + '%' }"
                          class="bg-green d-flex align-center justify-center text-caption font-weight-bold dark-text select-none text-truncate"
                          :title="'Ämtli: ' + c.taskOccupancyRate + '%'">
                          {{ c.taskOccupancyRate > 8 ? c.taskOccupancyRate + "% Ämtli" : c.taskOccupancyRate + "%" }}
                        </div>
                        <div
                          v-if="c.freeOccupancyRate > 0"
                          :style="{ width: c.freeOccupancyRate + '%' }"
                          class="bg-free d-flex align-center justify-center text-caption font-weight-bold grey--text text--lighten-1 select-none text-truncate"
                          :title="'Freie Kapazität: ' + c.freeOccupancyRate + '%'">
                          {{ c.freeOccupancyRate > 8 ? c.freeOccupancyRate + "% Frei" : c.freeOccupancyRate + "%" }}
                        </div>
                      </div>

                      <div class="d-flex justify-space-between text-caption grey--text mt-1">
                        <div>Arbeitszeit: {{ +(c.totalShiftMinutes / 60).toFixed(1) }}h ({{ c.shifts }} Schichten)</div>
                        <div>No-Shows: {{ c.noShows }} ({{ c.noShowRate }}%)</div>
                      </div>
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>

            <div class="chart-card mt-4">
              <div class="chart-title">Center-Vergleichstabelle</div>
              <v-data-table dark dense class="transparent-table" :headers="centerHeaders" :items="centerBreakdownItems" hide-default-footer>
                <template v-slot:item.totalOccupancyRate="{ item }">
                  <div class="d-flex align-center" style="min-width: 100px">
                    <span class="mr-2 font-weight-bold" :class="occupancyColorClass(item.totalOccupancyRate)">{{ item.totalOccupancyRate }}%</span>
                    <v-progress-linear
                      dark
                      height="5"
                      :value="item.totalOccupancyRate"
                      :color="occupancyColor(item.totalOccupancyRate)"
                      style="width: 40px"
                      class="rounded"></v-progress-linear>
                  </div>
                </template>
                <template v-slot:item.appointmentOccupancyRate="{ item }">
                  <span class="blue--text text--lighten-2">{{ item.appointmentOccupancyRate }}%</span>
                </template>
                <template v-slot:item.taskOccupancyRate="{ item }">
                  <span class="green--text text--lighten-2">{{ item.taskOccupancyRate }}%</span>
                </template>
                <template v-slot:item.freeOccupancyRate="{ item }">
                  <span class="grey--text">{{ item.freeOccupancyRate }}%</span>
                </template>
                <template v-slot:item.noShowRate="{ item }">
                  <v-chip x-small :color="noShowColor(item.noShowRate)" dark>{{ item.noShowRate }}%</v-chip>
                </template>
              </v-data-table>
            </div>
          </div>
        </v-tab-item>

        <!-- TAB 7: ROHDATEN -->
        <v-tab-item>
          <div class="pa-4">
            <div class="d-flex align-center mb-3" style="gap: 12px">
              <v-text-field v-model="search" label="Suchen" dark hide-details dense outlined append-icon="mdi-magnify" style="max-width: 360px" />
              <v-spacer />
              <v-btn small color="primary" @click="exportRaw"><v-icon left small>mdi-download</v-icon>Excel</v-btn>
            </div>
            <v-data-table
              dark
              dense
              class="transparent-table"
              :headers="rawHeaders"
              :items="filteredRaw"
              :items-per-page="20"
              sort-by="date"
              sort-desc>
              <template v-slot:item.status="{ item }">
                <v-chip x-small :color="statusColor(item.status)" dark>{{ statusLabel(item.status) }}</v-chip>
              </template>
            </v-data-table>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <!-- Employee Detail Dialog -->
    <v-dialog v-model="empDialog" max-width="900px" scrollable>
      <v-card v-if="selectedEmpDetails" class="glass-dialog-card px-4 py-3" dark>
        <v-card-title class="d-flex align-center pb-2 border-bottom-light">
          <v-avatar size="64" class="mr-4 elevation-4 border-white-thick">
            <img
              :src="selectedEmpDetails.picture ? '/img/profilbilder/' + selectedEmpDetails.picture : '/img/default-avatar.png'"
              style="object-fit: cover" />
          </v-avatar>
          <div>
            <div class="headline font-weight-black white--text">{{ selectedEmpDetails.name }}</div>
            <div class="text-subtitle-2 grey--text text--lighten-1">Mitarbeiter-Detailprofil</div>
          </div>
          <v-spacer />
          <v-btn icon @click="empDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="py-4" style="max-height: 75vh">
          <!-- KPI Row (Responsive Flex Container) -->
          <div class="d-flex flex-wrap mb-4" style="gap: 8px">
            <div class="emp-detail-kpi detail-kpi-blue flex-grow-1" style="flex: 1 1 140px">
              <div class="lbl">Termine</div>
              <div class="val">{{ selectedEmpDetails.appointments }}</div>
              <div class="sub">Gesamt erledigt</div>
            </div>

            <div class="emp-detail-kpi detail-kpi-purple flex-grow-1" style="flex: 1 1 140px">
              <div class="lbl">Arbeitszeit</div>
              <div class="val">{{ selectedEmpDetails.totalWorkHours }}h</div>
              <div class="sub">{{ selectedEmpDetails.shifts }} Schichten</div>
            </div>

            <div class="emp-detail-kpi detail-kpi-orange flex-grow-1" style="flex: 1 1 140px">
              <div class="lbl">Auslastung</div>
              <div class="val" :class="occupancyColorClass(selectedEmpDetails.occupancyRate)">{{ selectedEmpDetails.occupancyRate }}%</div>
              <div class="sub">Schichtbelegung</div>
            </div>

            <div class="emp-detail-kpi detail-kpi-green flex-grow-1" style="flex: 1 1 140px">
              <div class="lbl">Task-Quote</div>
              <div class="val">{{ selectedEmpDetails.taskCompletionRate }}%</div>
              <div class="sub">{{ selectedEmpDetails.tasksDone }} / {{ selectedEmpDetails.tasks }} erledigt</div>
            </div>

            <div class="emp-detail-kpi detail-kpi-red flex-grow-1" style="flex: 1 1 140px">
              <div class="lbl">No-Show-Rate</div>
              <div class="val">{{ selectedEmpDetails.noShowRate }}%</div>
              <div class="sub">{{ selectedEmpDetails.noShows }} No-Shows</div>
            </div>
          </div>

          <!-- Detailed Charts -->
          <v-row>
            <v-col cols="12" md="6">
              <div class="chart-card mb-4" style="height: 280px">
                <div class="chart-title">Termin-Arten (Verteilung)</div>
                <apexchart v-if="hasSpecialTypes" type="donut" height="220" :options="empDetailsDonutOpts" :series="empDetailsDonutSeries" />
                <div v-else class="empty-state-small py-12 text-center grey--text text-caption">Keine spezifischen Termintypen</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="chart-card mb-4" style="height: 280px">
                <div class="chart-title">Wochentag-Verteilung</div>
                <apexchart type="bar" height="220" :options="empDetailsWdOpts" :series="empDetailsWdSeries" />
              </div>
            </v-col>
            <v-col cols="12">
              <div class="chart-card" style="height: 260px">
                <div class="chart-title">Stündliche Aktivität (Termine)</div>
                <apexchart type="area" height="200" :options="empDetailsHourlyOpts" :series="empDetailsHourlySeries" />
              </div>
            </v-col>
          </v-row>

          <!-- Searchable Completed Appointments & Notes List (Raw Data Segment) -->
          <v-card class="chart-card mt-4" flat>
            <div class="d-flex align-center justify-space-between mb-3 flex-wrap" style="gap: 8px">
              <div class="chart-title mb-0">Durchgeführte Termine & Notizen ({{ filteredEmployeeAppointments.length }})</div>
              <v-text-field
                v-model="empApptSearch"
                label="Termine / Notizen durchsuchen..."
                dark
                hide-details
                dense
                outlined
                append-icon="mdi-magnify"
                style="max-width: 280px" />
            </div>

            <v-data-table
              dark
              dense
              class="transparent-table mt-2"
              :headers="empApptHeaders"
              :items="filteredEmployeeAppointments"
              :items-per-page="5"
              no-data-text="Keine Termine für diesen Zeitraum gefunden">
              <template v-slot:item.date="{ item }">
                <span class="white--text font-weight-medium">{{ formatDate(item.date) }}</span>
              </template>
              <template v-slot:item.time="{ item }">
                <span class="grey--text text--lighten-2 text-caption">{{ item.start }} - {{ item.end }}</span>
              </template>
              <template v-slot:item.customer="{ item }">
                <div class="py-1">
                  <div class="font-weight-medium white--text text-caption">{{ item.customer }}</div>
                  <div class="grey--text text-caption" style="font-size: 0.75rem !important; opacity: 0.8">{{ item.title }}</div>
                </div>
              </template>
              <template v-slot:item.specialType="{ item }">
                <v-chip x-small dark color="blue-grey lighten-1">{{ translateType(item.specialType) }}</v-chip>
              </template>
              <template v-slot:item.status="{ item }">
                <v-chip x-small :color="statusColor(item.status)" dark>{{ statusLabel(item.status) }}</v-chip>
              </template>
              <template v-slot:item.description="{ item }">
                <div
                  v-if="item.description"
                  class="note-box py-1 px-2 rounded my-1 text-caption font-italic grey--text text--lighten-3"
                  style="background: rgba(255, 255, 255, 0.06); max-width: 320px; line-height: 1.3">
                  <v-icon x-small color="orange lighten-2" class="mr-1">mdi-message-text-outline</v-icon>
                  {{ item.description }}
                </div>
                <span v-else class="grey--text text--darken-1 text-caption font-italic">–</span>
              </template>
            </v-data-table>
          </v-card>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-overlay :value="loading" absolute opacity="0.4">
      <v-progress-circular indeterminate size="64" color="primary" />
    </v-overlay>
  </div>
</template>

<script>
import api from "../../../api"

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"]

const TYPE_TRANSLATIONS = {
  trial: "Probetraining",
  intro: "Einführungstraining",
  followup: "Folgetermin",
  check: "Checkup / Check",
  milon: "Milon Training",
  nutrition: "Ernährungsberatung",
  consultation: "Beratungsgespräch",
  workout: "Workout",
  trainingplan: "Trainingsplan",
  anamnese: "Anamnese / Checkup",
  new_contract: "Vertragserstellung",
  upgrade: "Vertrag / Upgrade",
  normal: "Normaler Termin",
  first: "Erstberatung",
  follow: "Folgetermin",
}

export default {
  name: "WcDashboard",
  props: {
    studios: { type: Array, required: true },
    datePickerRange: { type: Array, required: true },
  },
  data() {
    return {
      loading: false,
      data: null,
      activeTab: 0,
      search: "",
      heatmapMetric: "appointments",
      selectedEmployees: [],
      selectedEmpDetails: null,
      empDialog: false,
      empApptSearch: "",
    }
  },
  async created() {
    await this.loadData()
  },
  watch: {
    studios() {
      this.loadData()
    },
    datePickerRange() {
      if (this.datePickerRange[0] && this.datePickerRange[1]) this.loadData()
    },
  },
  computed: {
    totals() {
      if (!this.data)
        return {
          totalAppointments: 0,
          totalTasks: 0,
          totalShifts: 0,
          totalEmployees: 0,
          avgAppointmentsPerDay: 0,
          taskCompletionRate: 0,
          noShowRate: 0,
          deletedCount: 0,
          statusDistribution: {},
          tasksDone: 0,
          tasksOpen: 0,
          completionsCount: 0,
        }
      return this.data.totals
    },
    // --- TAB 1: ÜBERSICHT ---
    trendSeries() {
      if (!this.data) return []
      const d = this.data.dailyStats
      return [
        { name: "Termine", data: d.map((x) => ({ x: x.date, y: x.appointments })) },
        { name: "Aufgaben", data: d.map((x) => ({ x: x.date, y: x.tasks })) },
      ]
    },
    trendOpts() {
      return {
        chart: { toolbar: { show: false }, foreColor: "#fff" },
        stroke: { curve: "smooth", width: 2 },
        xaxis: { type: "datetime" },
        colors: ["#48c6ef", "#a18cd1"],
        fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0 } },
        dataLabels: { enabled: false },
        grid: { borderColor: "rgba(255,255,255,0.1)" },
        legend: { labels: { colors: "#fff" } },
      }
    },
    donutSeries() {
      if (!this.data) return []
      const s = this.totals.statusDistribution || {}
      return [s.planned || 0, s.checkedin || 0, s.done || 0, s.noshow || 0, s.cancelled || 0]
    },
    donutOpts() {
      return {
        chart: { foreColor: "#fff" },
        labels: ["Geplant", "Erschienen", "Erledigt", "No-Show", "Abgesagt"],
        colors: ["#6f86d6", "#48c6ef", "#43e97b", "#f5576c", "#ffa726"],
        legend: { position: "bottom", labels: { colors: "#fff" } },
        dataLabels: { style: { colors: ["#fff"] } },
        plotOptions: { pie: { donut: { size: "55%" } } },
      }
    },
    noShowTrendSeries() {
      if (!this.data) return []
      const d = this.data.dailyStats
      return [
        { name: "No-Shows", data: d.map((x) => ({ x: x.date, y: x.noShows })) },
        { name: "Absagen", data: d.map((x) => ({ x: x.date, y: x.cancelled })) },
      ]
    },
    noShowTrendOpts() {
      return {
        chart: { toolbar: { show: false }, foreColor: "#fff" },
        stroke: { curve: "smooth", width: 3 },
        xaxis: { type: "datetime" },
        colors: ["#f5576c", "#ffa726"],
        dataLabels: { enabled: false },
        grid: { borderColor: "rgba(255,255,255,0.1)" },
        markers: { size: 3 },
        legend: { labels: { colors: "#fff" } },
      }
    },
    hourlyBarSeries() {
      if (!this.data) return []
      const hourMap = {}
      this.data.hourlyHeatmap.forEach((c) => {
        hourMap[c.hour] = (hourMap[c.hour] || 0) + c.appointments
      })
      const hours = Object.keys(hourMap).sort((a, b) => a - b)
      return [{ name: "Termine", data: hours.map((h) => hourMap[h]) }]
    },
    hourlyBarOpts() {
      if (!this.data) return {}
      const hourMap = {}
      this.data.hourlyHeatmap.forEach((c) => {
        hourMap[c.hour] = (hourMap[c.hour] || 0) + c.appointments
      })
      const hours = Object.keys(hourMap).sort((a, b) => a - b)
      return {
        chart: { toolbar: { show: false }, foreColor: "#fff" },
        plotOptions: { bar: { borderRadius: 4, columnWidth: "60%" } },
        colors: ["#48c6ef"],
        dataLabels: { enabled: false },
        xaxis: { categories: hours.map((h) => h + ":00"), labels: { style: { colors: "#fff" } } },
        yaxis: { labels: { style: { colors: "#fff" } } },
        grid: { borderColor: "rgba(255,255,255,0.1)" },
      }
    },

    // --- TAB 2: MITARBEITER ---
    topEmployees() {
      if (!this.data) return []
      return [...this.data.employeeStats].sort((a, b) => b.appointments - a.appointments).slice(0, 5)
    },
    empBarSeries() {
      if (!this.data) return []
      const sorted = [...this.data.employeeStats].sort((a, b) => b.appointments - a.appointments).slice(0, 15)
      return [{ name: "Termine", data: sorted.map((e) => ({ x: e.name, y: e.appointments })) }]
    },
    empBarOpts() {
      return {
        chart: { toolbar: { show: false }, foreColor: "#fff" },
        plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
        colors: ["#48c6ef"],
        dataLabels: { enabled: true, style: { colors: ["#fff"] } },
        grid: { borderColor: "rgba(255,255,255,0.1)" },
        xaxis: { labels: { style: { colors: "#fff" } } },
        yaxis: { labels: { style: { colors: "#fff" } } },
      }
    },
    empHeaders() {
      return [
        { text: "Mitarbeiter", value: "name" },
        { text: "Auslastung", value: "occupancyRate" },
        { text: "Termine", value: "appointments" },
        { text: "Tasks", value: "tasks" },
        { text: "Schichten", value: "shifts" },
        { text: "Arbeitsstd.", value: "totalWorkHours" },
        { text: "Task-Quote", value: "taskCompletionRate" },
        { text: "No-Show", value: "noShowRate" },
        { text: "Ø Termine/Schicht", value: "avgAppointmentsPerShift" },
      ]
    },
    // Comparison Chart Logic
    comparisonChartSeries() {
      if (!this.selectedEmployees || this.selectedEmployees.length < 2) return []
      return this.selectedEmployees.map((emp) => {
        return {
          name: emp.name,
          data: [emp.appointments, emp.shifts, emp.totalWorkHours, emp.occupancyRate, emp.taskCompletionRate, emp.noShowRate],
        }
      })
    },
    comparisonChartOpts() {
      return {
        chart: { type: "bar", toolbar: { show: false }, foreColor: "#fff" },
        plotOptions: { bar: { horizontal: false, columnWidth: "55%", borderRadius: 4 } },
        dataLabels: { enabled: true, style: { fontSize: "10px", colors: ["#fff"] } },
        stroke: { show: true, width: 2, colors: ["transparent"] },
        xaxis: {
          categories: ["Termine", "Schichten", "Arbeitsstd. (h)", "Auslastung (%)", "Task-Quote (%)", "No-Show (%)"],
          labels: { style: { colors: "#fff" } },
        },
        yaxis: { labels: { style: { colors: "#fff" } } },
        fill: { opacity: 1 },
        colors: ["#48c6ef", "#a18cd1", "#43e97b", "#38f9d7", "#ffa726", "#f5576c"],
        grid: { borderColor: "rgba(255,255,255,0.1)" },
        legend: { position: "bottom", labels: { colors: "#fff" }, fontSize: "10px" },
        tooltip: { theme: "dark" },
      }
    },
    // Dialog Detail Computation
    hasSpecialTypes() {
      if (!this.selectedEmpDetails || !this.selectedEmpDetails.specialTypeCounts) return false
      return Object.values(this.selectedEmpDetails.specialTypeCounts).some((v) => v > 0)
    },
    empDetailsDonutSeries() {
      if (!this.selectedEmpDetails || !this.selectedEmpDetails.specialTypeCounts) return []
      return Object.values(this.selectedEmpDetails.specialTypeCounts)
    },
    empDetailsDonutOpts() {
      if (!this.selectedEmpDetails || !this.selectedEmpDetails.specialTypeCounts) return {}
      const labels = Object.keys(this.selectedEmpDetails.specialTypeCounts).map((l) => {
        const key = String(l).toLowerCase().trim()
        return TYPE_TRANSLATIONS[key] || l
      })
      return {
        chart: { foreColor: "#fff" },
        labels,
        colors: ["#48c6ef", "#a18cd1", "#43e97b", "#ffa726", "#f5576c"],
        legend: { position: "bottom", labels: { colors: "#fff" }, fontSize: "9px" },
        dataLabels: { enabled: true, style: { fontSize: "10px" } },
        plotOptions: { pie: { donut: { size: "55%" } } },
      }
    },
    empDetailsWdSeries() {
      if (!this.selectedEmpDetails || !this.selectedEmpDetails.weekdayCounts) return []
      return [{ name: "Termine", data: this.selectedEmpDetails.weekdayCounts }]
    },
    empDetailsWdOpts() {
      return {
        chart: { toolbar: { show: false }, foreColor: "#fff" },
        plotOptions: { bar: { borderRadius: 3, columnWidth: "50%" } },
        colors: ["#a18cd1"],
        dataLabels: { enabled: false },
        xaxis: {
          categories: ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"],
          labels: { style: { colors: "#fff" } },
        },
        yaxis: { labels: { style: { colors: "#fff" } } },
        grid: { borderColor: "rgba(255,255,255,0.1)" },
      }
    },
    empDetailsHourlySeries() {
      if (!this.selectedEmpDetails || !this.selectedEmpDetails.hourlyCounts) return []
      const data = []
      for (let h = 6; h <= 22; h++) {
        data.push(this.selectedEmpDetails.hourlyCounts[h] || 0)
      }
      return [{ name: "Termine", data }]
    },
    empDetailsHourlyOpts() {
      const categories = []
      for (let h = 6; h <= 22; h++) {
        categories.push(h + ":00")
      }
      return {
        chart: { toolbar: { show: false }, foreColor: "#fff" },
        stroke: { curve: "smooth", width: 2 },
        colors: ["#48c6ef"],
        fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0 } },
        dataLabels: { enabled: false },
        xaxis: {
          categories,
          labels: { style: { colors: "#fff" } },
        },
        yaxis: { labels: { style: { colors: "#fff" } } },
        grid: { borderColor: "rgba(255,255,255,0.1)" },
      }
    },
    // Filtered Employee Raw Appointments for Dialog Table
    filteredEmployeeAppointments() {
      if (!this.selectedEmpDetails || !this.data || !this.data.rawAppointments) return []
      const rawList = this.data.rawAppointments.filter((a) => a.employee === this.selectedEmpDetails.name)
      if (!this.empApptSearch) return rawList
      const term = this.empApptSearch.toLowerCase()
      return rawList.filter((a) => `${a.title} ${a.customer} ${a.description} ${a.date}`.toLowerCase().includes(term))
    },
    empApptHeaders() {
      return [
        { text: "Datum", value: "date" },
        { text: "Uhrzeit", value: "time" },
        { text: "Kunde / Titel", value: "customer" },
        { text: "Terminart", value: "specialType" },
        { text: "Status", value: "status" },
        { text: "Notizen / Infos", value: "description" },
      ]
    },

    // --- TAB 3: ÄMTLI ---
    tplBarSeries() {
      if (!this.data) return []
      const items = [...this.data.templateStats].slice(0, 10)
      return [{ name: "Erledigt %", data: items.map((t) => ({ x: t.title, y: t.completionRate })) }]
    },
    tplBarOpts() {
      return {
        chart: { toolbar: { show: false }, foreColor: "#fff" },
        plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
        colors: ["#43e97b"],
        dataLabels: { enabled: true, formatter: (v) => v + "%", style: { colors: ["#fff"] } },
        grid: { borderColor: "rgba(255,255,255,0.1)" },
        xaxis: { max: 100, labels: { style: { colors: "#fff" } } },
        yaxis: { labels: { style: { colors: "#fff" } } },
      }
    },
    taskTrendSeries() {
      if (!this.data) return []
      const d = this.data.dailyStats
      return [
        { name: "Tasks", data: d.map((x) => ({ x: x.date, y: x.tasks })) },
        { name: "Erledigt", data: d.map((x) => ({ x: x.date, y: x.tasksDone })) },
      ]
    },
    taskTrendOpts() {
      return {
        chart: { toolbar: { show: false }, foreColor: "#fff" },
        stroke: { curve: "smooth", width: 2 },
        xaxis: { type: "datetime" },
        colors: ["#a18cd1", "#43e97b"],
        fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0 } },
        dataLabels: { enabled: false },
        grid: { borderColor: "rgba(255,255,255,0.1)" },
        legend: { labels: { colors: "#fff" } },
      }
    },
    tplHeaders() {
      return [
        { text: "Vorlage", value: "title" },
        { text: "Kategorie", value: "category" },
        { text: "Frequenz", value: "frequency" },
        { text: "Verwendet", value: "usageCount" },
        { text: "Erledigt", value: "doneCount" },
        { text: "Quote", value: "completionRate" },
        { text: "Ø / Tag", value: "avgPerDay" },
      ]
    },

    // --- TAB 4: HEATMAP ---
    hmSeries() {
      if (!this.data) return []
      const WDNAMES = ["Sonntag", "Samstag", "Freitag", "Donnerstag", "Mittwoch", "Dienstag", "Montag"]
      const grid = {}
      for (let w = 0; w < 7; w++) grid[w] = {}
      this.data.hourlyHeatmap.forEach((c) => {
        grid[c.weekday][c.hour] = (grid[c.weekday][c.hour] || 0) + c[this.heatmapMetric]
      })
      const series = []
      for (let w = 6; w >= 0; w--) {
        const row = { name: WDNAMES[6 - w], data: [] }
        for (let h = 6; h <= 22; h++) row.data.push({ x: h + ":00", y: grid[w][h] || 0 })
        series.push(row)
      }
      return series
    },
    hmOpts() {
      const isStaffing = this.heatmapMetric === "staffing"
      const ranges = isStaffing
        ? [
            { from: 0, to: 0, color: "#1e1e30", name: "0 Mitarbeiter (Nicht betreut)" },
            { from: 1, to: 1, color: "#2d4a6b", name: "1 Mitarbeiter" },
            { from: 2, to: 2, color: "#4a7ba6", name: "2 Mitarbeiter" },
            { from: 3, to: 3, color: "#16a085", name: "3 Mitarbeiter" },
            { from: 4, to: 100, color: "#2ecc71", name: "4+ Mitarbeiter" },
          ]
        : [
            { from: 0, to: 0, color: "#1a1a2e", name: "Keine" },
            { from: 1, to: 3, color: "#2d4a6b", name: "Wenig" },
            { from: 4, to: 8, color: "#4a7ba6", name: "Mittel" },
            { from: 9, to: 15, color: "#48c6ef", name: "Gut" },
            { from: 16, to: 1000, color: "#50fa7b", name: "Sehr gut" },
          ]
      return {
        chart: { toolbar: { show: false }, foreColor: "#fff" },
        dataLabels: { enabled: true, style: { colors: ["#fff"], fontSize: "10px" } },
        colors: isStaffing ? ["#2ecc71"] : ["#48c6ef"],
        xaxis: { labels: { style: { colors: "#fff" } } },
        plotOptions: {
          heatmap: {
            radius: 4,
            colorScale: { ranges },
          },
        },
      }
    },

    // --- TAB 5: WOCHENTAG ---
    weekdayItems() {
      if (!this.data) return []
      return this.data.weekdayStats.map((w) => ({ ...w, name: this.weekdayName(w.weekday) }))
    },
    wdBarSeries() {
      if (!this.data) return []
      return [{ name: "Ø Termine", data: this.data.weekdayStats.map((w) => w.avgAppointments) }]
    },
    wdBarOpts() {
      return {
        chart: { toolbar: { show: false }, foreColor: "#fff" },
        plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
        colors: ["#48c6ef"],
        dataLabels: { enabled: false },
        xaxis: { categories: this.data ? this.data.weekdayStats.map((w) => this.weekdayName(w.weekday)) : [], labels: { style: { colors: "#fff" } } },
        yaxis: { labels: { style: { colors: "#fff" } } },
        grid: { borderColor: "rgba(255,255,255,0.1)" },
      }
    },
    wdNoShowSeries() {
      if (!this.data) return []
      return [{ name: "No-Show %", data: this.data.weekdayStats.map((w) => w.noShowRate) }]
    },
    wdNoShowOpts() {
      return {
        chart: { toolbar: { show: false }, foreColor: "#fff" },
        plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
        colors: ["#f5576c"],
        dataLabels: { enabled: true, formatter: (v) => v + "%", style: { colors: ["#fff"] } },
        xaxis: { categories: this.data ? this.data.weekdayStats.map((w) => this.weekdayName(w.weekday)) : [], labels: { style: { colors: "#fff" } } },
        yaxis: { labels: { style: { colors: "#fff" } } },
        grid: { borderColor: "rgba(255,255,255,0.1)" },
      }
    },
    wdHeaders() {
      return [
        { text: "Tag", value: "name" },
        { text: "Termine", value: "appointments" },
        { text: "Ø Termine", value: "avgAppointments" },
        { text: "Tasks", value: "tasks" },
        { text: "No-Shows", value: "noShows" },
        { text: "No-Show Rate", value: "noShowRate" },
        { text: "Schichten", value: "shifts" },
      ]
    },

    // --- TAB 6: SCHICHT- & CENTER-ANALYSE ---
    centerBreakdownItems() {
      if (!this.data || !this.data.centerBreakdown) return []
      return Object.values(this.data.centerBreakdown).map((c) => {
        const noShowRate = c.appointments > 0 ? +((c.noShows / c.appointments) * 100).toFixed(1) : 0
        return {
          ...c,
          name: this.getStudioName(c.id),
          noShowRate,
        }
      })
    },
    centerShiftSeries() {
      if (!this.data || !this.data.centerShiftTypes) return []
      const morningData = []
      const intermediateData = []
      const lateData = []

      const studioIds = Object.keys(this.data.centerShiftTypes)
      studioIds.forEach((id) => {
        const counts = this.data.centerShiftTypes[id]
        morningData.push({ x: this.getStudioName(id), y: counts.morning || 0 })
        intermediateData.push({ x: this.getStudioName(id), y: counts.intermediate || 0 })
        lateData.push({ x: this.getStudioName(id), y: counts.late || 0 })
      })

      return [
        { name: "Frühschichten", data: morningData },
        { name: "Zwischenschichten", data: intermediateData },
        { name: "Spätschichten", data: lateData },
      ]
    },
    centerShiftOpts() {
      return {
        chart: { toolbar: { show: false }, foreColor: "#fff" },
        plotOptions: { bar: { horizontal: false, columnWidth: "55%", borderRadius: 4 } },
        colors: ["#b7f8db", "#ffa726", "#a18cd1"],
        dataLabels: { enabled: true, style: { fontSize: "10px", colors: ["#fff"] } },
        xaxis: { labels: { style: { colors: "#fff" } } },
        yaxis: { labels: { style: { colors: "#fff" } } },
        grid: { borderColor: "rgba(255,255,255,0.1)" },
        legend: { position: "bottom", labels: { colors: "#fff" }, fontSize: "10px" },
      }
    },
    centerHeaders() {
      return [
        { text: "Center Name", value: "name" },
        { text: "Auslastung", value: "totalOccupancyRate" },
        { text: "Termine-Anteil", value: "appointmentOccupancyRate" },
        { text: "Ämtli-Anteil", value: "taskOccupancyRate" },
        { text: "Frei-Anteil", value: "freeOccupancyRate" },
        { text: "Schichten", value: "shifts" },
        { text: "No-Show Rate", value: "noShowRate" },
      ]
    },

    // --- TAB 7: ROHDATEN ---
    filteredRaw() {
      if (!this.data) return []
      if (!this.search) return this.data.rawAppointments
      const term = this.search.toLowerCase()
      return this.data.rawAppointments.filter((a) => `${a.title} ${a.employee} ${a.customer} ${a.date}`.toLowerCase().includes(term))
    },
    rawHeaders() {
      return [
        { text: "Datum", value: "date" },
        { text: "Start", value: "start" },
        { text: "Ende", value: "end" },
        { text: "Titel", value: "title" },
        { text: "Mitarbeiter", value: "employee" },
        { text: "Kunde", value: "customer" },
        { text: "Status", value: "status" },
      ]
    },
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        this.data = await api.shiftManagement.fetchWorkCalendarStatistics({
          studios: this.studios.map((s) => s._id),
          range: this.datePickerRange,
        })
      } catch (e) {
        this.$toast && this.$toast.error("Fehler beim Laden der Statistiken")
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    weekdayName(w) {
      return WEEKDAYS[w] || "?"
    },
    rateColor(v) {
      return v >= 80 ? "green" : v >= 50 ? "orange" : "red"
    },
    noShowColor(v) {
      return v <= 5 ? "green" : v <= 15 ? "orange" : "red"
    },
    statusColor(s) {
      return { planned: "blue-grey", checkedin: "cyan", done: "green", noshow: "red", cancelled: "orange" }[s] || "grey"
    },
    statusLabel(s) {
      return { planned: "Geplant", checkedin: "Erschienen", done: "Erledigt", noshow: "No-Show", cancelled: "Abgesagt" }[s] || s
    },
    getStudioName(id) {
      const st = this.studios.find((s) => s._id === id)
      return st ? st.name : "Unbekanntes Center"
    },
    openEmployeeDetails(emp) {
      this.selectedEmpDetails = emp
      this.empApptSearch = ""
      this.empDialog = true
    },
    occupancyColor(v) {
      return v >= 85 ? "red" : v >= 50 ? "green" : "cyan"
    },
    occupancyColorClass(v) {
      return v >= 85 ? "red--text text--lighten-2" : v >= 50 ? "green--text text--lighten-2" : "cyan--text text--lighten-2"
    },
    translateType(val) {
      if (!val) return "Normaler Termin"
      const key = String(val).toLowerCase().trim()
      return TYPE_TRANSLATIONS[key] || val
    },
    formatDate(d) {
      if (!d) return ""
      const parts = d.split("-")
      if (parts.length === 3) {
        return `${parts[2]}.${parts[1]}.${parts[0]}`
      }
      return d
    },
    exportRaw() {
      if (!this.data) return
      try {
        const XLSX = require("xlsx")
        const rows = this.filteredRaw.map((a) => ({
          Datum: a.date,
          Start: a.start,
          Ende: a.end,
          Titel: a.title,
          Mitarbeiter: a.employee,
          Kunde: a.customer,
          Status: this.statusLabel(a.status),
        }))
        const ws = XLSX.utils.json_to_sheet(rows)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, "Termine")
        XLSX.writeFile(wb, "WorkCalendar_Rohdaten.xlsx")
      } catch (e) {
        console.error(e)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.wc-dashboard {
  position: relative;
}

.cursor-pointer {
  cursor: pointer;
}

.hover-underline:hover {
  text-decoration: underline;
}

.select-none {
  user-select: none;
}

.dark-text {
  color: #1a1a2e !important;
}

/* Gradients for Stacked Segmented progress bar */
.bg-blue {
  background: linear-gradient(90deg, #48c6ef, #6f86d6) !important;
}
.bg-green {
  background: linear-gradient(90deg, #43e97b, #38f9d7) !important;
}
.bg-free {
  background: rgba(255, 255, 255, 0.12) !important;
  color: rgba(255, 255, 255, 0.5) !important;
}

.kpi-card {
  border-radius: 16px;
  padding: 20px;
  color: #fff;
  height: 100%;
  backdrop-filter: blur(12px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  transition: transform 0.2s ease;
  &:hover {
    transform: translateY(-2px);
  }
  .kpi-label {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    opacity: 0.85;
  }
  .kpi-value {
    font-size: 2rem;
    font-weight: 700;
    margin-top: 4px;
    line-height: 1;
  }
  .kpi-sub {
    font-size: 0.75rem;
    opacity: 0.8;
    margin-top: 6px;
  }
}

.kpi-1 {
  background: linear-gradient(135deg, #48c6ef 0%, #6f86d6 100%);
}
.kpi-2 {
  background: linear-gradient(135deg, #f5576c 0%, #ff6b6b 100%);
}
.kpi-3 {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
  color: #1a1a2e;
}
.kpi-4 {
  background: linear-gradient(135deg, #667db6 0%, #0082c8 100%);
}
.kpi-5 {
  background: linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%);
  color: #1a1a2e;
}
.kpi-6 {
  background: linear-gradient(135deg, #e056fd 0%, #ff7675 100%);
}
.kpi-7 {
  background: linear-gradient(135deg, #b7f8db 0%, #50a7c2 100%);
}
.kpi-8 {
  background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
  color: #1a1a2e;
}

.glass-card {
  background: rgba(255, 255, 255, 0.04) !important;
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px !important;
}

.chart-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 16px;
  height: 100%;
  .chart-title {
    color: #fff;
    font-weight: 600;
    font-size: 0.9rem;
    margin-bottom: 12px;
  }
}

.transparent-table {
  background: transparent !important;
  ::v-deep .v-data-table__wrapper {
    background: transparent;
  }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.95rem;
}

.top-slot-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  color: #fff;
  transition: background 0.2s ease;
  &:hover {
    background: rgba(255, 255, 255, 0.04);
  }
  &:last-child {
    border-bottom: none;
  }
  .rank {
    font-size: 1.25rem;
    font-weight: 700;
    opacity: 0.6;
    min-width: 32px;
  }
  .slot-title {
    font-weight: 600;
    font-size: 0.9rem;
  }
  .slot-sub {
    font-size: 0.75rem;
    opacity: 0.65;
  }
  .slot-value {
    font-size: 1.25rem;
    font-weight: 700;
    color: #48c6ef;
  }
}

/* Comparison CSS */
.comparison-panel {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 18px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(12px);
}

.comparison-cards-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 250px;
  overflow-y: auto;
  padding-right: 4px;
}

.comparison-emp-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 12px;
  transition: all 0.2s ease;
  &:hover {
    background: rgba(255, 255, 255, 0.07);
    border-color: rgba(255, 255, 255, 0.1);
  }
}

.comp-emp-name {
  font-weight: 600;
  font-size: 0.9rem;
  color: #fff;
}

.comp-emp-sub {
  color: rgba(255, 255, 255, 0.5);
}

.comp-stats-grid {
  .comp-stat-val {
    font-size: 1rem;
    line-height: 1.2;
  }
  .comp-stat-lbl {
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: rgba(255, 255, 255, 0.5);
    margin-top: 2px;
  }
}

/* Dialog Detail CSS */
.glass-dialog-card {
  background: rgba(26, 26, 46, 0.95) !important;
  backdrop-filter: blur(25px);
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  border-radius: 20px !important;
}

.border-bottom-light {
  border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
}

.border-white-thick {
  border: 2px solid rgba(255, 255, 255, 0.7) !important;
}

.emp-detail-kpi {
  padding: 16px;
  border-radius: 12px;
  color: #fff;
  height: 100%;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
  .lbl {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    opacity: 0.8;
  }
  .val {
    font-size: 1.5rem;
    font-weight: 800;
    line-height: 1.2;
    margin-top: 2px;
  }
  .sub {
    font-size: 0.7rem;
    opacity: 0.7;
    margin-top: 4px;
  }
}

.detail-kpi-blue {
  background: linear-gradient(135deg, #2b5876 0%, #4e4376 100%);
}
.detail-kpi-purple {
  background: linear-gradient(135deg, #654ea3 0%, #eaafc8 100%);
}
.detail-kpi-green {
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  color: #0b221e !important;
  .val {
    color: #0b221e !important;
  }
  .sub,
  .lbl {
    color: rgba(11, 34, 30, 0.85) !important;
  }
}
.detail-kpi-red {
  background: linear-gradient(135deg, #cb2d3e 0%, #ef473a 100%);
}
.detail-kpi-orange {
  background: linear-gradient(135deg, #f12711 0%, #f5af19 100%);
  color: #fff !important;
  .val {
    color: #fff !important;
  }
  .sub,
  .lbl {
    color: rgba(255, 255, 255, 0.85) !important;
  }
}

.empty-state-small {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: rgba(255, 255, 255, 0.4);
}
</style>
